// Libraries
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

// UI
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';

@Component({
  selector: 'lib-search-select',
  standalone: true,
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
    MatFormFieldModule,
    FormsModule,
    MatSelect,
    MatOption,
    NoDataComponent
  ]
})
export class SearchSelectComponent implements OnInit, OnChanges {
  // Components API
  @ViewChild('searchSelect') selectElement!: MatSelect;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @Input() items: any[] = [];
  @Input() selectedItem: string | null | undefined = null;
  @Input() label: string = 'Select Item';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() valueKey: string = '';
  @Input() labelKey: string = '';
  @Input() disabled: boolean = false;
  @Input() backgroundColor: string = 'transparent';
  @Input() valueColor: string = 'inherit';
  @Input() noFloatLabel: boolean = false;
  @Output() itemChanged = new EventEmitter<string | null>();

  // Variables
  searchValue: string = '';
  filteredItems: any[] = [];
  id = Math.floor(Math.random() * 100000);
  activeIndex: number = -1;

  /////////////////////////////
  // Angular Lifecycle Hooks //
  /////////////////////////////

  ngOnInit() {
    this.filteredItems = [...this.items];
  }

  ngAfterViewInit() {
    this.selectElement?.openedChange.subscribe((opened) => {
      if (opened) {
        this.focusSearchInput();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.filteredItems = [...this.items];

      if (this.selectedItem && this.items.length && !this.items.some(item => (this.valueKey ? item[this.valueKey] : item) == this.selectedItem)) {
        this.selectedItem = null;
      }
    }

    if (changes['selectedItem']?.currentValue) {
      this.selectedItem = changes['selectedItem'].currentValue;
    }
  }


  onSelectOpen() {
    this.searchValue = '';
    this.filteredItems = [...this.items];
    setTimeout(() => {
      this.focusSearchInput();
      this.initActiveIndex();
    }, 500);
  }

  filterItems(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const searchTerm = inputElement.value.toLowerCase();

    this.filteredItems = this.items.filter(item =>
      (this.labelKey ? item[this.labelKey] : item).toLowerCase().includes(searchTerm)
    );
    this.initActiveIndex();
  }

  onSearchFocus(): void {

  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.selectElement?.panelOpen) return;
    if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.selectItemAtIndex(this.activeIndex);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveSelection(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveSelection(-1);
    }
  }

  /** Moves selection up or down */
  private moveSelection(direction: number) {
    if (this.filteredItems.length === 0) return;

    const newIndex = this.activeIndex + direction;

    if (newIndex >= 0 && newIndex < this.filteredItems.length) {
      this.activeIndex = newIndex;
      this.highlightOption(newIndex);
    }
  }

  private initActiveIndex() {
    this.activeIndex = this.filteredItems.findIndex(item => (this.valueKey ? item[this.valueKey] : item) == this.selectedItem);
    if (this.activeIndex == -1)
      this.activeIndex = 0;
  }

  private highlightOption(index: number) {
    if (!this.filteredItems[index] || !this.selectElement) return;
    const optionId = `option-${this.id}-${index}`;
    const optionElement = document.getElementById(optionId);
    optionElement?.scrollIntoView({ block: 'nearest' });

    this.selectElement.value = this.valueKey ? this.filteredItems[index][this.valueKey] : this.filteredItems[index];
  }

  private selectItemAtIndex(index: number) {
    this.selectedItem = this.valueKey ? this.filteredItems[index][this.valueKey] : this.filteredItems[index];
    this.itemChanged.emit(this.selectedItem);
    this.selectElement.close();
  }

  inputKeyDown(event: KeyboardEvent) {
    // Escape/Tab must keep bubbling so MatSelect's own overlay can close and move focus
    // normally — swallowing them here left the panel (and its full-screen backdrop) stuck open,
    // blocking every click on the page until the user clicked exactly the right spot twice.
    if (!['ArrowDown', 'ArrowUp', 'Escape', 'Tab'].includes(event.key))
      event.stopPropagation();

    if (event.key == 'Enter') {
      event.preventDefault();
      this.selectItemAtIndex(this.activeIndex);
    }
  }

  onSelectionChanged(event: any) {
    if (event.value) {
      this.itemChanged.emit(event.value)
      this.selectElement.toggle(); // Close the select dropdown when an option is selected
    }
  }

  private focusSearchInput() {
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  compareFn = (a: any, b: any) => {
    if (!a || !b) return a === b;
    let _a = typeof a == "string" ? a : (this.valueKey ? a[this.valueKey] : a);
    let _b = typeof b == "string" ? b : (this.valueKey ? b[this.valueKey] : b);
    return _a == _b;
  };

  getLabel() {
    if (this.labelKey && this.selectedItem && Object.prototype.hasOwnProperty.call(this.selectedItem, this.labelKey))
      return this.selectedItem[this.labelKey as keyof typeof this.selectedItem];
    return this.selectedItem || "";
  }
}
